/**
 * Shared utilities for Chrome extension authentication integration
 */

import { useExtensionAuth } from 'src/hooks/useExtensionAuth';

export interface ExtensionAuthParams {
  source?: string | null;
  extensionId?: string | null;
}

/**
 * Parse URL parameters to check if request came from Chrome extension
 */
export function parseExtensionParams(searchParams?: URLSearchParams): ExtensionAuthParams {
  if (!searchParams) {
    if (typeof window !== 'undefined') {
      searchParams = new URLSearchParams(window.location.search);
    } else {
      return { source: null, extensionId: null };
    }
  }
  
  return {
    source: searchParams.get('source'),
    extensionId: searchParams.get('extensionId'),
  };
}

/**
 * Check if the current request is from Chrome extension
 */
export function isExtensionRequest(params: ExtensionAuthParams): boolean {
  return params.source === 'extension' && !!params.extensionId;
}

/**
 * Handle authentication success for extension requests
 * This is the shared logic that both /login and /auth/callback can use
 */
export async function handleExtensionAuthSuccess(
  params: ExtensionAuthParams,
  shareTokensWithExtension: () => Promise<boolean>,
  fallbackRedirect: () => void
): Promise<void> {
  if (!isExtensionRequest(params)) {
    // Not an extension request, use normal flow
    fallbackRedirect();
    return;
  }

  console.log('Handling extension auth success for extensionId:', params.extensionId);
  
  try {
    // Share tokens with extension
    const success = await shareTokensWithExtension();
    
    if (success) {
      console.log('Tokens shared with extension successfully');
      
      // Close this tab since extension now has auth
      // Use setTimeout to ensure the message is sent before closing
      setTimeout(() => {
        window.close();
      }, 500);
    } else {
      console.warn('Failed to share tokens with extension');
      fallbackRedirect();
    }
  } catch (error) {
    console.error('Extension token sharing failed:', error);
    fallbackRedirect();
  }
}

/**
 * Custom hook that combines extension detection and auth handling
 */
export function useExtensionAuthHandler() {
  const { shareTokensWithExtension } = useExtensionAuth();
  
  const handleAuthSuccess = async (fallbackRedirect: () => void) => {
    const params = parseExtensionParams();
    await handleExtensionAuthSuccess(params, shareTokensWithExtension, fallbackRedirect);
  };
  
  return {
    isExtensionRequest: isExtensionRequest(parseExtensionParams()),
    handleAuthSuccess,
    parseExtensionParams,
  };
}